import get from 'lodash/get';
import { ITEM_STATES } from '../constants/itemAccessors';
import {
  CANCELLED,
  FAILED,
  IN_PROGRESS,
  mapItemStatesToStatuses,
  SHIPPED,
} from '../util/mapStateToStatus';

export function orderInfoV1(order = {}, tenant = '') {
  return {
    tenant,
    orderNumber: order.orderNumber,
    orderCustomOrderData: { ...(order.customOrderData || {}), ux: undefined },
    isMigrated: order.migrated,
    currency: order.orderPriceSummary?.currency,
    fractionDigits: order.orderPriceSummary?.fractionDigits,
  };
}

export function reorderableItemOrderInfoV1(
  reorderableLineItem = {},
  tenant = ''
) {
  return {
    tenant,
    orderNumber: reorderableLineItem.orderNumber,
    orderCustomOrderData: {
      ...(reorderableLineItem.externalUseData || {}),
      ux: undefined,
    },
    isMigrated: reorderableLineItem.migrated,
    currency: reorderableLineItem.merchandisingData?.currencyCode,
    fractionDigits: reorderableLineItem.merchandisingData?.fractionDigits,
  };
}

export function orderInfoV2(order = {}, tenant = '') {
  const { orderPriceSummary, migrated } = order;
  return {
    tenant,
    orderNumber: order.orderNumber,
    orderCustomOrderData: { ...(order.customOrderData || {}), ux: undefined },
    isMigrated: migrated,
    // For migrated orders, we don't have the taxed list prices.
    // If we leave those as undefined, it will raise errors in mod-father.
    // Alternatively we can set a default value of 0, but that may be interpreted incorrectly.
    // To simplify this, for migrated orders we set the list prices to be the
    // same as the discounted prices
    // This is documented in the Mod Hosts Technical Documentation so that clients are aware
    orderTotal: {
      untaxedDiscountedCents:
        orderPriceSummary?.orderTotalDiscountedPrice?.untaxed,
      taxedDiscountedCents: orderPriceSummary?.orderTotalDiscountedPrice?.taxed,
      untaxedListCents: migrated
        ? orderPriceSummary?.orderTotalDiscountedPrice?.untaxed
        : orderPriceSummary?.orderTotalListPrice?.untaxed,
      taxedListCents: migrated
        ? orderPriceSummary?.orderTotalDiscountedPrice?.taxed
        : orderPriceSummary?.orderTotalListPrice?.taxed,
    },
    productTotal: {
      untaxedDiscountedCents:
        orderPriceSummary?.orderProductDiscountedPrice?.untaxed,
      taxedDiscountedCents:
        orderPriceSummary?.orderProductDiscountedPrice?.taxed,
      untaxedListCents: migrated
        ? orderPriceSummary?.orderProductDiscountedPrice?.untaxed
        : orderPriceSummary?.orderProductListPrice?.untaxed,
      taxedListCents: migrated
        ? orderPriceSummary?.orderProductDiscountedPrice?.taxed
        : orderPriceSummary?.orderProductListPrice?.taxed,
    },
    shippingTotal: {
      untaxedDiscountedCents:
        orderPriceSummary?.orderShippingDiscountedPrice?.untaxed,
      taxedDiscountedCents:
        orderPriceSummary?.orderShippingDiscountedPrice?.taxed,
      untaxedListCents: migrated
        ? orderPriceSummary?.orderShippingDiscountedPrice?.untaxed
        : orderPriceSummary?.orderShippingListPrice?.untaxed,
      taxedListCents: migrated
        ? orderPriceSummary?.orderShippingDiscountedPrice?.taxed
        : orderPriceSummary?.orderShippingListPrice?.taxed,
    },
    currency: order.orderPriceSummary?.currency,
    fractionDigits: order.orderPriceSummary?.fractionDigits,
  };
}

export function profileInfoV1(email, phone) {
  return {
    phone: phone || '',
    email: email || '',
  };
}

export function lineItemInfoV1(lineItem = {}) {
  return {
    lineItemId: lineItem.id,
    lineItemCustomOrderData: {
      ...(lineItem.customOrderData || {}),
      ux: undefined,
    },
    lineItemQuantity: lineItem.quantity,
  };
}

export function reorderableItemLineItemInfoV1(reorderableLineItem = {}) {
  return {
    lineItemId: reorderableLineItem.lineItemId,
    lineItemCustomOrderData: {
      ...(reorderableLineItem.externalUseData || {}),
      ux: undefined,
    },
    lineItemQuantity: reorderableLineItem.originalOrderedQuantity,
  };
}

function getProductVersion(lineItem = {}) {
  if (
    !lineItem ||
    lineItem.productVersion === undefined ||
    lineItem.productVersion === null ||
    typeof lineItem.productVersion !== 'number'
  ) {
    return -1;
  }
  return lineItem.productVersion;
}

function getReorderableProductVersion(lineItem = {}) {
  if (
    !lineItem ||
    lineItem.productData?.productVersion === undefined ||
    lineItem.productData?.productVersion === null ||
    typeof lineItem.productData?.productVersion !== 'number'
  ) {
    return -1;
  }
  return lineItem.productData?.productVersion;
}

export function lineItemInfoV2(lineItem = {}) {
  // NOTE: 2 properties are evaluated before an empty array default.
  // This is due to the fact that the `lineItemInfoV2` fn is used to package
  // up lineItemGroup contracts as well. The `item` input to itemGroup differs from
  // the `item` input from the ItemModWrappers. This should be cleaned up when we have
  // consistent response models from a BFF for OH and OD.
  const itemAttributes =
    lineItem?.displayProductAttributes || lineItem?.attributes || [];

  return {
    lineItemId: lineItem.id,
    lineItemCustomOrderData: {
      ...(lineItem.customOrderData || {}),
      ux: undefined,
    },
    lineItemQuantity: lineItem.quantity,
    groupId: lineItem.groupInfo?.groupId || '',
    lineItemProduct: {
      // fallback to empty values to over-write potentially null properties. ModFather does not currently handle null properties safely. Need Typescript.
      productKey: lineItem?.productKey || '',
      productVersion: getProductVersion(lineItem),
      selectedAttributes: itemAttributes,
    },
  };
}

export function reorderableItemLineItemInfoV2(reorderableLineItem = {}) {
  return {
    lineItemId: reorderableLineItem.lineItemId,
    lineItemCustomOrderData: {
      ...(reorderableLineItem.externalUseData || {}),
      ux: undefined,
    },
    lineItemQuantity: reorderableLineItem.originalOrderedQuantity,
    groupId: reorderableLineItem.groupId || '',
    lineItemProduct: {
      productKey: reorderableLineItem.productData?.productKey || '',
      productVersion: getReorderableProductVersion(reorderableLineItem),
      selectedAttributes:
        reorderableLineItem.productData?.productAttributes || [],
    },
  };
}

export function cartTemplateRefV1(lineItem = {}) {
  const cartTemplateRef = lineItem.cartTemplateRef || {};
  return {
    hasCartTemplate: !!cartTemplateRef.cartTemplateId,
    cartTemplateId: cartTemplateRef.cartTemplateId || '',
    tenant: cartTemplateRef.tenant || '',
  };
}

function effectiveStatuses(statuses = {}) {
  const results = {};
  for (const { statusTranslationKey } of statuses) {
    results[statusTranslationKey] = true;
  }
  return results;
}

export function lineItemOrderStatusV1(lineItem = {}) {
  const itemStates = get(lineItem, ITEM_STATES, []);
  const statuses = mapItemStatesToStatuses(itemStates, lineItem.isDeliverable);
  const statusesInEffect = effectiveStatuses(statuses);
  return {
    isInProgress: !!statusesInEffect[IN_PROGRESS],
    isCancelled: !!statusesInEffect[CANCELLED],
    isShipped: !!statusesInEffect[SHIPPED],
    isFailed: !!statusesInEffect[FAILED],
  };
}

export function lineItemFulfillmentHoldsV2(lineItem = {}) {
  const holds = lineItem.fulfillmentHoldsV2 || [];

  return {
    hasHolds: holds.length > 0,
    hasActiveHolds: holds.filter(hold => hold.active).length > 0,
    holds,
  };
}

export function lineItemDesignInfoV1(lineItem = {}) {
  const docRefUrl = lineItem?.documentUrl || '';
  const livePreviewUrl = lineItem?.livePreviewUrl || '';

  return {
    docRefUrl,
    livePreviewUrl,
  };
}

export function reorderableItemLineItemDesignInfoV1(reorderableLineItem = {}) {
  return {
    docRefUrl: reorderableLineItem.designData?.docRefUrl || '',
    livePreviewUrl: reorderableLineItem.designData?.livePreviewUrl || '',
  };
}

export function reorderablelineItemMerchandisingInfoV1(
  selectedPricedQuantity = {}
) {
  return {
    merchandisableQuantities: [selectedPricedQuantity] || [],
  };
}

export function lineItemShippingInfoV1(lineItem = {}) {
  // NOTE: 2 properties are evaluated before an empty array default.
  // This is due to the fact that the `lineItemInfoV2` fn is used to package
  // up lineItemGroup contracts as well. The `item` input to itemGroup differs from
  // the `item` input from the ItemModWrappers. This should be cleaned up when we have
  // consistent response models from a BFF for OH and OD.
  // fallback to empty strings to over-write potentially null properties. ModFather does not currently handle null properties safely. Need Typescript.
  const promisedArrivalDate =
    lineItem?.promisedArrivalDate || lineItem?.cmrd || '';

  const trackingUrl =
    lineItem?.trackingData?.length > 0
      ? lineItem.trackingData[0].trackingUrl || ''
      : lineItem?.trackingUrl || '';

  return {
    promisedArrivalDate,
    trackingUrl,
  };
}

/**
 * `itemGroupType` is not available post order currently, so it is set to an empty string.
 *
 * @param {*} order The BOP (oh-ui-route) order response object.
 * @param {*} groupId The group ID to build this contract for.
 * @returns A lineItemGroupInfoV1 data env object for use with mods.
 */
export function lineItemGroupInfoV1(order = {}, groupId = '') {
  const orderItemGroups = order?.itemGroups || [];
  const itemGroupData =
    orderItemGroups.find(group => group.groupId === groupId) || {};

  // Not a huge fan of this but there is a lot of divergence in how BOP surfaces pricing compared to Vistacart.
  const currency =
    itemGroupData?.grossDiscountedCents?.currencyCode || undefined;
  const fractionDigits =
    itemGroupData?.grossDiscountedCents?.fractionDigits || undefined;

  const netListCents = itemGroupData?.netListCents?.centAmount || undefined;
  const netDiscountedCents =
    itemGroupData?.netDiscountedCents?.centAmount || undefined;
  const grossListCents = itemGroupData?.grossListCents?.centAmount || undefined;
  const grossDiscountedCents =
    itemGroupData?.grossDiscountedCents?.centAmount || undefined;

  const pricingData = {
    currency,
    fractionDigits,
    netListCents,
    netDiscountedCents,
    grossListCents,
    grossDiscountedCents,
  };
  const groupCustomOrderData = {
    ...(itemGroupData?.customOrderData || {}),
    ux: undefined,
  };

  return {
    groupId,
    itemGroupType: '',
    pricingData,
    groupCustomOrderData,
  };
}

export function orderItemGroupItemsInfoV1(order = {}, groupId = '') {
  const orderItemGroups = order?.itemGroups || [];
  const orderItems = order?.lineItems || [];

  const itemGroupData =
    orderItemGroups.find(group => group.groupId === groupId) || {};
  const lineItemWithinGroup = itemGroupData?.lineItemIds || [];

  const items = [];

  lineItemWithinGroup.forEach(itemId => {
    const lineItem = orderItems.find(item => item.id === itemId);
    const lineItemInfoV2Data = lineItemInfoV2(lineItem);
    const lineItemDesignInfoV1Data = lineItemDesignInfoV1(lineItem);
    const lineItemShippingInfoV1Data = lineItemShippingInfoV1(lineItem);

    items.push({
      ...lineItemInfoV2Data,
      ...lineItemDesignInfoV1Data,
      ...lineItemShippingInfoV1Data,
    });
  });

  return { items };
}

export const commonVpDependenciesV1 = {
  swan: { major: 3 },
};
